import React, { useState } from 'react';
import type { GameState, Monster, UserLanguage } from '../api';
import { GameDialog } from './GameDialog';
import { MonsterTokenFrontSvg } from './MonsterTokenSvg';
import './Monsters.css';
import '../styles/DunwichTerrorTrack.css';
import { DUNWICH_HORROR_REWARD, DunwichHorrorToken, dunwichHorrorReferenceForm } from './DunwichHorrorToken';

// Printed reference token, independent of the Horror's current combat card.
const dunwichHorror: Monster = {
  name: 'The Dunwich Horror', type: 'Spawn', dimensionSign: 'Crescent',
  movementType: 'Special', awareness: 2, toughness: 5,
  horrorRating: -3, horrorDamage: 2, combatRating: 0, combatDamage: 0,
  specialCombatRating: true, specialCombatDamage: true,
};

/** A vector redraw of the tentacled creature on the original Dunwich Horror token. */
function DunwichTerrorIcon({ active }: { active: boolean }) {
  return <svg className="dunwich-track-icon" data-active={active} viewBox="0 0 104 68" aria-hidden="true" focusable="false">
    <rect className="dunwich-track-icon-frame" x="1" y="1" width="102" height="66" rx="7" />
    <g className="dunwich-track-creature" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 59 C12 58 8 52 13 47 C17 43 22 48 18 51 C25 55 28 45 26 40
        C16 41 7 32 12 25 C15 21 19 24 16 28 C19 34 28 32 31 36
        C27 24 20 22 23 15 C26 10 31 14 28 18 C33 23 38 23 39 33
        C40 22 32 15 37 7 C40 3 45 6 42 10 C40 17 50 20 49 30
        C55 18 50 11 57 8 C62 6 66 11 61 13 C56 17 63 24 57 33
        C68 28 69 19 77 20 C84 21 82 27 77 26 C72 29 71 38 65 39
        C78 35 82 40 89 34 C94 30 89 27 92 24 C99 21 101 33 95 38
        C88 45 75 40 77 49 C79 55 88 51 87 47 C94 43 98 54 90 58
        C82 64 71 55 68 59 C61 65 54 60 49 63 C42 66 35 59 30 61 Z" />
      <path d="M31 54 C38 46 32 38 40 36 C47 34 44 45 40 50
        M51 56 C60 49 50 43 56 39 C61 37 65 44 61 49
        M22 59 C31 58 29 51 33 48 M68 59 C75 59 74 51 69 48" />
    </g>
    <g className="dunwich-track-eyes">
      <ellipse cx="43" cy="32" rx="3" ry="2" transform="rotate(-20 43 32)" />
      <ellipse cx="56" cy="33" rx="3" ry="2" transform="rotate(20 56 33)" />
    </g>
  </svg>;
}

export function DunwichTerrorTrack({ value = 0, language, style, onOpen, includeMiskatonicCards = false }: {
  includeMiskatonicCards?: boolean;
  value?: GameState['dunwichHorrorTerrorTrack'];
  language: UserLanguage;
  style?: React.CSSProperties;
  onOpen: () => void;
}) {
  const [open, setOpen] = useState(false);
  const russian = language === 'Ru';
  const title = russian ? 'Трек ужаса Данвича' : 'Dunwich Terror track';
  const count = `${value}/3`;
  const referenceMonster: Monster = { ...dunwichHorror, additionalInfo: russian
    ? 'Вместо движения бросьте кубик. На 4–6 добавьте жетон безысходности на трек безысходности.\nПеред проверкой Ужаса против Ужаса Данвича вытяните карту Ужаса Данвича, чтобы определить его характеристики.'
    : `Instead of moving, roll a die. On a 4–6, add a doom token to the doom track.\nBefore making a Horror check against the Dunwich Horror, draw a Dunwich Horror card to determine its attributes.\n${DUNWICH_HORROR_REWARD}` };
  return <>
    <button type="button" className="dunwich-terror-track" style={style} aria-label={`${title}: ${count}`}
      aria-haspopup="dialog" aria-expanded={open} onClick={event => {
        event.currentTarget.focus({ preventScroll: true });
        onOpen();
        setOpen(true);
      }}>
      <span className="dunwich-track-icons" aria-hidden="true">
        {[1, 2, 3].map(space => <DunwichTerrorIcon key={space} active={space <= value} />)}
      </span>
      <span className="dunwich-track-caption" aria-hidden="true">{title} · {count}</span>
    </button>
    {open && <GameDialog title={title} variant="location" onClose={() => setOpen(false)}>
      <div className="dunwich-terror-info">
        <p className="dunwich-terror-count">{russian ? 'Текущий уровень' : 'Current track'}: {count}</p>
        <p>{russian
          ? 'Когда все три деления этого трека заполнены, Ужас Данвича пробуждается на Сторожевом холме (Sentinel Hill).'
          : 'When all three spaces on this track are filled, the Dunwich Horror awakens at Sentinel Hill.'}</p>
        <section aria-label={russian ? 'Ужас Данвича' : 'The Dunwich Horror'}>
          <h3>{russian ? 'Ужас Данвича' : 'The Dunwich Horror'}</h3>
          <div className="dunwich-horror-monster">
            <div className="dunwich-horror-side" role="img" aria-label={russian ? 'Ужас Данвича — лицевая сторона' : 'The Dunwich Horror front'}>
              <div aria-hidden="true"><MonsterTokenFrontSvg monster={referenceMonster} /></div>
            </div>
            <div className="dunwich-horror-side" role="img" aria-label={russian ? 'Ужас Данвича — оборотная сторона' : 'The Dunwich Horror back'}>
              <div aria-hidden="true"><DunwichHorrorToken monster={referenceMonster} russian={russian} /></div>
            </div>
          </div>
        </section>
        <section aria-label={russian ? 'Боевые карты' : 'Combat cards'}>
          <h3>{russian ? 'Боевые карты' : 'Combat cards'}</h3>
          <div className="dunwich-horror-combat-cards">
            {Array.from({ length: includeMiskatonicCards ? 13 : 7 }, (_, index) => index + 1).map(number =>
              <DunwichHorrorToken key={number} number={number} monster={dunwichHorrorReferenceForm(number)} russian={russian} />)}
          </div>
        </section>
      </div>
    </GameDialog>}
  </>;
}
